import React from 'react'

// FW Version cell with live OTA progress.
//
// The admin userDetail mapping stamps each module (pcb) with otaPercent /
// otaStatus / otaAt / sentOTAVersion (from the backend ota-manager's
// OTA_PROGRESS events and the per-airing stamp). otaAt is the freshness
// anchor: within OTA_FRESH_MS the OTA info is live and worth surfacing next
// to the version; older than that it's history — render the version alone,
// exactly as the cell always did.
//
// Rendering rules (fresh otaAt only):
//   - the module already reports a version >= the target → plain version.
//     It ARRIVED; there is nothing in flight to report. Without this guard a
//     module happily running the new image still showed an OTA badge for the
//     full freshness window (bench cycle-4, 2026-07-30).
//   - terminal outcome (STATUS_OK / OTA_COMPLETE / SKIP_OTA, or percent >= 100)
//     → version + badge
//   - otaPercent set and < 100 → "version → target · NN%" + thin progress bar
//   - anything else (e.g. status without a percent yet) → plain version

const OTA_FRESH_MS = 15 * 60 * 1000

// Terminal states — the OTA attempt is over; show the outcome, not progress.
// OTA_COMPLETE is the backend's label for a 100% report (the firmware's final
// frame carries a percent and no Status_MSG, so the backend names it).
const TERMINAL_STATUSES = new Set(['STATUS_OK', 'OTA_COMPLETE', 'SKIP_OTA'])
// Terminal states that mean SUCCESS (green). SKIP_OTA stays amber: the device
// declined the offer, which is an outcome worth noticing, not a success.
const SUCCESS_STATUSES = new Set(['STATUS_OK', 'OTA_COMPLETE'])

export const isOtaFresh = (otaAt: any, now: number = Date.now()): boolean => {
    if (!otaAt) return false
    const t = new Date(otaAt).getTime()
    if (Number.isNaN(t)) return false
    return now - t <= OTA_FRESH_MS
}

// Mirrors the backend's normalizeFirmwareVersion (common.utils.js) for
// comparison only. Kept deliberately faithful to that implementation:
//   3-char  -> insert a 0 before the last digit: '302' -> 3002, '224' -> 2204
//   5-digit -> fold the legacy App encoding: '20010' -> 2010, '20221' -> 2221
//   otherwise parse as-is; any non-numeric input -> 0
export const encodeFw = (v: any): number => {
    const s = String(v ?? '').trim()
    if (!/^\d+$/.test(s)) return 0
    if (s.length === 3) return parseInt(s[0] + s[1] + '0' + s[2], 10) || 0
    const n = parseInt(s, 10)
    if (Number.isNaN(n)) return 0
    if (n >= 10000 && n <= 99999) {
        return Math.floor(n / 10000) * 1000 + Math.floor((n % 10000) / 100) * 100 + (n % 100)
    }
    return n
}

const FirmwareCell = ({ pcb }: { pcb: any }) => {
    const version = pcb?.firmwareVersion || '--'
    if (!isOtaFresh(pcb?.otaAt)) return <>{version}</>

    // Already arrived: the reported version meets or exceeds what was offered.
    const target = pcb?.sentOTAVersion
    if (target && encodeFw(version) >= encodeFw(target)) return <>{version}</>

    const percent = typeof pcb?.otaPercent === 'number' ? pcb.otaPercent : null
    const status = pcb?.otaStatus || ''

    if (TERMINAL_STATUSES.has(status) || (percent != null && percent >= 100)) {
        // Green only for an actual success word, or a 100% report that carries
        // no terminal word at all (older firmware). A terminal-but-not-success
        // status (SKIP_OTA) must stay amber.
        const ok = SUCCESS_STATUSES.has(status) ||
            (!TERMINAL_STATUSES.has(status) && percent != null && percent >= 100)
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {version}
                <span
                    data-testid="ota-badge"
                    title={TERMINAL_STATUSES.has(status)
                        ? `OTA result: ${status}`
                        : 'OTA reached 100% — awaiting firmware version confirmation'}
                    style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                        background: ok ? '#ECFDF3' : '#FFFAEB',
                        color: ok ? '#067647' : '#B54708',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {status || '100%'}
                </span>
            </span>
        )
    }

    if (percent == null) return <>{version}</>

    const clamped = Math.max(0, Math.min(100, percent))
    return (
        <span data-testid="ota-progress" style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ whiteSpace: 'nowrap' }}>
                {version}{' '}
                <span style={{ color: '#1a73e8', fontWeight: 600 }}>
                    → {target ? `${target} · ` : ''}{percent}%
                </span>
            </span>
            <span style={{ display: 'block', width: 72, height: 3, background: '#E4E7EC', borderRadius: 2, overflow: 'hidden' }}>
                <span style={{ display: 'block', width: `${clamped}%`, height: '100%', background: '#1a73e8' }} />
            </span>
        </span>
    )
}

export default FirmwareCell
